package dev.wyns.ventrova.ui.web

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppEnvironment
import dev.wyns.ventrova.app.AppUiState
import dev.wyns.ventrova.app.AppViewModel
import dev.wyns.ventrova.app.RadiusOptions
import dev.wyns.ventrova.app.SuggestionForm
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.app_name
import dev.wyns.ventrova.resources.filter_clear_all
import dev.wyns.ventrova.resources.filter_distance
import dev.wyns.ventrova.resources.filter_how_plant_based
import dev.wyns.ventrova.resources.filter_kind_of_place
import dev.wyns.ventrova.resources.filter_open_now
import dev.wyns.ventrova.resources.filter_show_places
import dev.wyns.ventrova.resources.filters
import dev.wyns.ventrova.resources.ic_check
import dev.wyns.ventrova.resources.ic_close
import dev.wyns.ventrova.resources.ic_near_me_filled
import dev.wyns.ventrova.resources.onboarding_skip_web
import dev.wyns.ventrova.resources.onboarding_tagline
import dev.wyns.ventrova.resources.onboarding_use_location_web
import dev.wyns.ventrova.resources.suggest_address_placeholder
import dev.wyns.ventrova.resources.suggest_address_short
import dev.wyns.ventrova.resources.suggest_cancel
import dev.wyns.ventrova.resources.suggest_error
import dev.wyns.ventrova.resources.suggest_intro
import dev.wyns.ventrova.resources.suggest_name_placeholder
import dev.wyns.ventrova.resources.suggest_name_short
import dev.wyns.ventrova.resources.suggest_note_placeholder
import dev.wyns.ventrova.resources.suggest_optional
import dev.wyns.ventrova.resources.suggest_place
import dev.wyns.ventrova.resources.suggest_send_long
import dev.wyns.ventrova.resources.suggest_thanks
import dev.wyns.ventrova.resources.suggest_thanks_body_short
import dev.wyns.ventrova.resources.suggest_what_is_vegan_short
import dev.wyns.ventrova.resources.action_close
import dev.wyns.ventrova.ui.components.DrawnPlatformControls
import dev.wyns.ventrova.ui.components.LevelGlyph
import dev.wyns.ventrova.ui.components.LevelLegend
import dev.wyns.ventrova.ui.components.StrictnessSlider
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.components.radiusLabel
import dev.wyns.ventrova.ui.components.stopDescription
import dev.wyns.ventrova.ui.theme.LevelPalettes
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.description
import dev.wyns.ventrova.ui.theme.displayStyle
import dev.wyns.ventrova.ui.theme.icon
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.palette
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Web flavour of the switch (smaller, green) and segmented control. */
private val WebControls = DrawnPlatformControls(
    toggleOnColor = VentrovaColors.Open,
    toggleWidth = 36.dp,
    toggleHeight = 20.dp,
    segmentHeight = 28.dp,
    segmentCorner = 7.dp,
)

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun WebFilterPopover(state: AppUiState, viewModel: AppViewModel, onClose: () -> Unit, modifier: Modifier) {
    val filters = state.filters
    Column(
        modifier
            .shadow(18.dp, RoundedCornerShape(16.dp))
            .background(Color.White, RoundedCornerShape(16.dp))
            .padding(start = 18.dp, end = 18.dp, top = 16.dp, bottom = 18.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.filters), style = displayStyle(17), modifier = Modifier.weight(1f))
            Text(
                stringResource(Res.string.filter_clear_all),
                style = bodyStyle(13.sp, FontWeight.SemiBold, VentrovaColors.Accent),
                modifier = Modifier.handCursor().clickable(onClick = viewModel::clearFilters),
            )
        }
        Row(Modifier.padding(top = 16.dp), verticalAlignment = Alignment.Bottom) {
            Text(stringResource(Res.string.filter_how_plant_based), style = bodyStyle(12.5.sp, FontWeight.SemiBold, VentrovaColors.Muted), modifier = Modifier.weight(1f))
            Text(stopDescription(filters.strictness), style = bodyStyle(13.5.sp, FontWeight.SemiBold))
        }
        StrictnessSlider(filters.strictness, viewModel::setStrictness, Modifier.padding(top = 10.dp), knobSize = 30.dp, glyphSize = 14.dp, labelWidth = 74.dp, labelSize = 11.5f)

        Text(stringResource(Res.string.filter_kind_of_place), style = bodyStyle(12.5.sp, FontWeight.SemiBold, VentrovaColors.Muted), modifier = Modifier.padding(top = 16.dp, bottom = 8.dp))
        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            PlaceCategory.entries.forEach { category ->
                val active = category in filters.categories
                WebChip(active = active, onClick = { viewModel.toggleCategory(category) }, activeBackground = VentrovaColors.Ink, activeContent = Color.White, activeBorder = VentrovaColors.Ink, height = 30.dp) {
                    VIcon(category.icon, size = 16.dp)
                    Text(stringResource(category.label), style = bodyStyle(13.sp, color = androidx.compose.material3.LocalContentColor.current))
                    Text("${state.categoryCounts[category] ?: 0}", style = bodyStyle(13.sp, color = androidx.compose.material3.LocalContentColor.current.copy(alpha = 0.5f)))
                }
            }
        }

        Row(
            Modifier.padding(top = 16.dp).fillMaxWidth().handCursor().clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = viewModel::toggleOpenNow),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(stringResource(Res.string.filter_open_now), style = bodyStyle(13.5.sp, FontWeight.SemiBold), modifier = Modifier.weight(1f))
            WebControls.Toggle(filters.openNow, { viewModel.toggleOpenNow() }, Modifier)
        }

        Text(stringResource(Res.string.filter_distance), style = bodyStyle(12.5.sp, FontWeight.SemiBold, VentrovaColors.Muted), modifier = Modifier.padding(top = 16.dp, bottom = 8.dp))
        WebControls.SegmentedControl(
            options = RadiusOptions.map { radiusLabel(it) },
            selectedIndex = RadiusOptions.indexOf(filters.radiusKm),
            onSelect = { viewModel.setRadius(RadiusOptions[it]) },
            modifier = Modifier.fillMaxWidth().handCursor(),
        )

        Box(
            Modifier.padding(top = 16.dp).fillMaxWidth().height(38.dp).clip(RoundedCornerShape(10.dp)).background(VentrovaColors.Ink).handCursor().clickable(onClick = onClose),
            contentAlignment = Alignment.Center,
        ) {
            Text(pluralStringResource(Res.plurals.filter_show_places, state.nearby.size, state.nearby.size), style = bodyStyle(14.sp, FontWeight.SemiBold, Color.White))
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun WebSuggestModal(form: SuggestionForm, viewModel: AppViewModel, onClose: () -> Unit) {
    Box(
        Modifier
            .fillMaxSize()
            .background(VentrovaColors.Ink.copy(alpha = 0.35f))
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onClose),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            Modifier
                .widthIn(max = 520.dp)
                .padding(20.dp)
                .heightIn(max = 760.dp)
                .shadow(30.dp, RoundedCornerShape(20.dp))
                .background(VentrovaColors.Paper, RoundedCornerShape(20.dp))
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {}
                .verticalScroll(rememberScrollState()),
        ) {
            Row(Modifier.padding(start = 22.dp, end = 22.dp, top = 20.dp, bottom = 6.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.suggest_place), style = displayStyle(22), modifier = Modifier.weight(1f))
                Box(Modifier.size(34.dp).clip(RoundedCornerShape(8.dp)).handCursor().clickable(onClick = onClose), contentAlignment = Alignment.Center) {
                    VIcon(Res.drawable.ic_close, size = 20.dp, tint = VentrovaColors.Ink)
                }
            }
            if (form.sent) {
                Column(Modifier.fillMaxWidth().padding(start = 22.dp, end = 22.dp, top = 30.dp, bottom = 28.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                    Box(Modifier.size(64.dp).background(LevelPalettes.Vegan.soft, CircleShape), contentAlignment = Alignment.Center) {
                        VIcon(Res.drawable.ic_check, size = 34.dp, tint = VentrovaColors.Accent)
                    }
                    Text(stringResource(Res.string.suggest_thanks), style = displayStyle(22), modifier = Modifier.padding(top = 14.dp))
                    Text(stringResource(Res.string.suggest_thanks_body_short, form.name.trim()), style = bodyStyle(14.5.sp, color = VentrovaColors.Secondary), modifier = Modifier.padding(top = 6.dp))
                    Box(Modifier.padding(top = 20.dp).height(40.dp).clip(RoundedCornerShape(10.dp)).background(VentrovaColors.Ink).handCursor().clickable(onClick = onClose).padding(horizontal = 22.dp), contentAlignment = Alignment.Center) {
                        Text(stringResource(Res.string.action_close), style = bodyStyle(14.sp, FontWeight.SemiBold, Color.White))
                    }
                }
                return@Column
            }
            Column(Modifier.padding(start = 22.dp, end = 22.dp, top = 8.dp, bottom = 22.dp), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                Text(stringResource(Res.string.suggest_intro), style = bodyStyle(14.sp, color = VentrovaColors.Secondary))
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    LabeledField(stringResource(Res.string.suggest_name_short), form.name, stringResource(Res.string.suggest_name_placeholder), Modifier.weight(1f)) { value ->
                        viewModel.updateSuggestion { it.copy(name = value) }
                    }
                    LabeledField(stringResource(Res.string.suggest_address_short), form.address, stringResource(Res.string.suggest_address_placeholder), Modifier.weight(1f)) { value ->
                        viewModel.updateSuggestion { it.copy(address = value) }
                    }
                }
                Text(stringResource(Res.string.filter_kind_of_place), style = bodyStyle(13.sp, FontWeight.SemiBold))
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    PlaceCategory.entries.forEach { category ->
                        val active = form.category == category
                        WebChip(active = active, onClick = { viewModel.updateSuggestion { it.copy(category = category) } }, activeBackground = VentrovaColors.Ink, activeContent = Color.White, activeBorder = VentrovaColors.Ink) {
                            VIcon(category.icon, size = 17.dp)
                            Text(stringResource(category.label), style = bodyStyle(13.5.sp, color = androidx.compose.material3.LocalContentColor.current))
                        }
                    }
                }
                Text(stringResource(Res.string.filter_how_plant_based), style = bodyStyle(13.sp, FontWeight.SemiBold))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    VeganLevel.entries.forEach { level ->
                        val active = form.level == level
                        Column(
                            Modifier
                                .weight(1f)
                                .clip(RoundedCornerShape(12.dp))
                                .background(if (active) level.palette.soft else Color.White)
                                .border(1.5.dp, if (active) level.palette.color else VentrovaColors.Line, RoundedCornerShape(12.dp))
                                .handCursor()
                                .clickable { viewModel.updateSuggestion { it.copy(level = level) } }
                                .padding(12.dp),
                            verticalArrangement = Arrangement.spacedBy(6.dp),
                        ) {
                            LevelGlyph(level, size = 16.dp, borderWidth = 3.dp)
                            Text(stringResource(level.label), style = bodyStyle(14.sp, FontWeight.SemiBold))
                            Text(stringResource(level.description), style = bodyStyle(12.sp, color = VentrovaColors.Secondary))
                        }
                    }
                }
                Column(verticalArrangement = Arrangement.spacedBy(5.dp)) {
                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text(stringResource(Res.string.suggest_what_is_vegan_short), style = bodyStyle(13.sp, FontWeight.SemiBold))
                        Text(stringResource(Res.string.suggest_optional), style = bodyStyle(13.sp, color = VentrovaColors.Muted))
                    }
                    WebTextField(form.note, stringResource(Res.string.suggest_note_placeholder), Modifier.fillMaxWidth().height(72.dp), singleLine = false) { value ->
                        viewModel.updateSuggestion { it.copy(note = value) }
                    }
                }
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    if (form.showError) Text(stringResource(Res.string.suggest_error), style = bodyStyle(13.sp, color = VentrovaColors.Error), modifier = Modifier.weight(1f))
                    else Box(Modifier.weight(1f))
                    Box(Modifier.height(40.dp).clip(RoundedCornerShape(10.dp)).handCursor().clickable(onClick = onClose).padding(horizontal = 16.dp), contentAlignment = Alignment.Center) {
                        Text(stringResource(Res.string.suggest_cancel), style = bodyStyle(14.sp, FontWeight.SemiBold))
                    }
                    Box(
                        Modifier.height(40.dp).clip(RoundedCornerShape(10.dp)).background(if (form.canSend) VentrovaColors.Ink else Color(0xFFB5B6B0)).handCursor().clickable(onClick = viewModel::sendSuggestion).padding(horizontal = 18.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(stringResource(Res.string.suggest_send_long), style = bodyStyle(14.sp, FontWeight.SemiBold, Color.White))
                    }
                }
            }
        }
    }
}

@Composable
private fun LabeledField(label: String, value: String, placeholder: String, modifier: Modifier, onValue: (String) -> Unit) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(5.dp)) {
        Text(label, style = bodyStyle(13.sp, FontWeight.SemiBold))
        WebTextField(value, placeholder, Modifier.fillMaxWidth().height(40.dp), singleLine = true, onValue = onValue)
    }
}

@Composable
private fun WebTextField(value: String, placeholder: String, modifier: Modifier, singleLine: Boolean, onValue: (String) -> Unit) {
    Box(
        modifier.background(Color.White, RoundedCornerShape(10.dp)).border(1.dp, VentrovaColors.LineStrong, RoundedCornerShape(10.dp)).padding(horizontal = 12.dp, vertical = if (singleLine) 0.dp else 10.dp),
        contentAlignment = if (singleLine) Alignment.CenterStart else Alignment.TopStart,
    ) {
        if (value.isEmpty()) Text(placeholder, style = bodyStyle(14.5.sp, color = VentrovaColors.Subtle))
        BasicTextField(value = value, onValueChange = onValue, singleLine = singleLine, textStyle = bodyStyle(14.5.sp), cursorBrush = SolidColor(VentrovaColors.Ink), modifier = Modifier.fillMaxWidth())
    }
}

/** First-visit dialog; shown only once (see AppViewModel.completeOnboarding). */
@Composable
internal fun WebOnboarding(env: AppEnvironment) {
    Box(
        Modifier.fillMaxSize().background(VentrovaColors.Canvas.copy(alpha = 0.72f)).clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {},
        contentAlignment = Alignment.Center,
    ) {
        Column(
            Modifier.widthIn(max = 460.dp).padding(20.dp).shadow(30.dp, RoundedCornerShape(24.dp)).background(VentrovaColors.Paper, RoundedCornerShape(24.dp)).padding(start = 32.dp, end = 32.dp, top = 32.dp, bottom = 24.dp),
        ) {
            Text(stringResource(Res.string.app_name), style = displayStyle(44, weight = FontWeight.ExtraBold, lineHeight = 0.95f))
            Text(stringResource(Res.string.onboarding_tagline), style = bodyStyle(17.sp, color = VentrovaColors.Secondary), modifier = Modifier.padding(top = 10.dp))
            LevelLegend(
                Modifier.padding(top = 22.dp).fillMaxWidth().background(VentrovaColors.SandLight, RoundedCornerShape(16.dp)).padding(16.dp),
                glyphSize = 16.dp,
                fontSize = 14.5.sp,
                spacing = 10.dp,
            )
            Row(Modifier.padding(top = 22.dp), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Row(
                    Modifier.weight(1f).height(46.dp).clip(RoundedCornerShape(12.dp)).background(VentrovaColors.Ink).handCursor().clickable(enabled = !env.awaitingPermission, onClick = env::useLocationFromOnboarding),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    VIcon(Res.drawable.ic_near_me_filled, size = 19.dp, tint = Color.White)
                    Text(stringResource(Res.string.onboarding_use_location_web), style = bodyStyle(15.sp, FontWeight.SemiBold, Color.White), modifier = Modifier.padding(start = 8.dp))
                }
                Box(
                    Modifier.height(46.dp).clip(RoundedCornerShape(12.dp)).border(1.dp, VentrovaColors.LineStrong, RoundedCornerShape(12.dp)).handCursor().clickable(onClick = env::skipLocationFromOnboarding).padding(horizontal = 18.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(stringResource(Res.string.onboarding_skip_web), style = bodyStyle(15.sp, FontWeight.SemiBold))
                }
            }
        }
    }
}

@Composable
internal fun WebToast(text: String, onShown: () -> Unit, modifier: Modifier) {
    LaunchedEffect(text) {
        delay(2200)
        onShown()
    }
    Box(modifier.height(40.dp).shadow(8.dp, RoundedCornerShape(10.dp)).background(VentrovaColors.Ink, RoundedCornerShape(10.dp)).padding(horizontal = 16.dp), contentAlignment = Alignment.Center) {
        Text(text, style = bodyStyle(14.sp, color = Color.White), textAlign = TextAlign.Center)
    }
}
